import apiClient from "@/api/apiClient";
import {
  createPaginatedRpcSupabaseQuery,
  createRpcQuery,
  spawnGetApiFunction,
  type ApiResourceConfig,
} from "@/utils/api";

import type {
  GetPaginatedWorkspaceDeviceModelFilesProps,
  PaginatedWorkspaceDeviceModelFile,
} from "@/api/resources/deviceModelFiles/types";
import type { BucketFile, FileMetaInformation } from "@/utils/storage/types";

export const BUCKET_DEVICE_MODEL_FILES = "device-model-files";
const table = "device_model_files";
const query = "*";

export const apiConfigDeviceModelFiles: ApiResourceConfig<typeof table, typeof query> = {
  table,
  query,
  searchables: ["title", "description", "locale", "path"],
  sortables: ["id", "title", "description", "locale", "shared_with_children"],
};

export const getDeviceModelFile = spawnGetApiFunction({ table, query });

export const getWorkspaceDeviceModelFiles = async (workspace_id: string) => {
  const { data, error } = await apiClient.rpc("get_workspace_device_model_files", { _workspace_id: workspace_id });
  if (error) throw error;

  return data;
};

export const getPaginatedWorkspaceDeviceModelFiles = async ({
  workspace_id,
  dataTableOptions,
  customFilters,
  customOrFilters,
}: GetPaginatedWorkspaceDeviceModelFilesProps) => {
  const { data, count, error } = await createPaginatedRpcSupabaseQuery<"get_workspace_device_model_files">({
    baseOptions: {
      rpcQuery: createRpcQuery(
        "get_workspace_device_model_files",
        {
          _workspace_id: workspace_id,
        },
        { count: "estimated" },
      ),
      searchables: apiConfigDeviceModelFiles.searchables,
      sortables: apiConfigDeviceModelFiles.sortables,
    },
    customFilters,
    customOrFilters,
    dataTableOptions,
  });
  if (error) throw error;
  const _data = data as PaginatedWorkspaceDeviceModelFile[] | null;
  return { data: _data, count };
};

export const getDeviceModelFilesById = async (device_model_id: string) => {
  const { data, error } = await apiClient.rpc("get_device_model_files", { _device_model_id: device_model_id });
  if (error) throw error;

  return data;
};

interface CreateDeviceModelFileProps {
  locale?: string | null;
  shared_with_children?: boolean;
  workspace_id: string;
  file: FileMetaInformation;
}
export const createDeviceModelFile = async ({
  locale,
  shared_with_children,
  file,
  workspace_id,
}: CreateDeviceModelFileProps) => {
  const { data, error } = await apiClient
    .from(table)
    .insert({
      bucket_id: BUCKET_DEVICE_MODEL_FILES,
      workspace_id,
      path: file.path,
      title: file.title,
      description: file.description,
      thumbnail: file.thumbnail,
      locale: locale,
      shared_with_children: shared_with_children,
    })
    .select(query)
    .maybeSingle();
  if (error) throw error;
  return data;
};

interface UpdateDeviceModelFileProps {
  id: string;
  title?: string | null;
  description?: string | null;
  thumbnail?: string | null;
  locale?: string | null;
  shared_with_children?: boolean;
}
export const updateDeviceModelFile = async ({
  id,
  title,
  description,
  thumbnail,
  locale,
  shared_with_children,
}: UpdateDeviceModelFileProps) => {
  const { data, error } = await apiClient
    .from(table)
    .update({
      title,
      description,
      thumbnail,
      locale,
      shared_with_children,
    })
    .match({
      id,
    })
    .select(query)
    .maybeSingle();
  if (error) throw error;
  return data;
};

export const deleteDeviceModelFile = async (file: BucketFile) => {
  // we don't throw here since it doesn't affect the user if the file isn't successfully deleted (only db entity matters)
  await apiClient.storage.from(file.bucket_id).remove([file.path]);
  if (file.thumbnail) await apiClient.storage.from(file.bucket_id).remove([file.thumbnail]);
  const { error, ...rest } = await apiClient.from(table).delete().match({
    id: file.id,
  });
  if (error) throw error;
  return { error, ...rest };
};
